import { ReviewPanel, AgentEmptyState } from '../ReviewPanel';
import { CheckpointActions } from '../CheckpointActions';
import type { AgentOutputRow } from '@/hooks/useJobProgress';

interface LoomPayload {
  design_system?: {
    typography?: {
      heading_font?: string;
      body_font?: string;
      mono_font?: string | null;
      scale?: string[];
    };
    colors?: {
      primary?: string;
      secondary?: string;
      accent?: string;
      detail?: string;
      neutral_scale?: string[];
    };
    spacing_scale?: number[];
    radius_scale?: number[];
    shadow_scale?: string[];
    border_style?: string;
  };
  pages?: {
    label: string;
    route?: string;
    layout_plan?: { component_kg_id: string; role?: string; notes?: string }[];
    components_used?: string[];
    law_references?: string[];
  }[];
  asset_manifest?: { name: string; usage: string; prompt: string; width?: number; height?: number }[];
  page_image_prompts?: { page_label: string; prompt: string }[];
}

interface LoomPanelProps {
  jobId: string;
  output: AgentOutputRow | undefined;
  isRunning?: boolean;
}

export function LoomPanel({ jobId, output, isRunning }: LoomPanelProps) {
  if (!output) {
    return (
      <ReviewPanel agent="loom" index={3}>
        <AgentEmptyState agent="loom" isRunning={isRunning} />
      </ReviewPanel>
    );
  }
  const payload = output.payload as LoomPayload;
  const ds = payload.design_system ?? {};
  const colors = ds.colors ?? {};

  return (
    <ReviewPanel
      agent="loom"
      index={3}
      iteration={output.iteration}
      reviewStatus={output.reviewStatus}
      subtitle="Technical descriptor"
    >
      <div className="space-y-8">
        <section className="space-y-3">
          <p className="smallcaps text-graphite">Design system — palette</p>
          <div className="flex flex-wrap gap-3">
            {(['primary', 'secondary', 'accent', 'detail'] as const).map((k) => {
              const hex = colors[k];
              if (!hex) return null;
              return (
                <div key={k} className="flex items-center gap-2">
                  <span
                    className="inline-block h-8 w-8 rounded-sm border border-ink/10"
                    style={{ background: hex }}
                    aria-hidden
                  />
                  <div className="font-mono text-[11px]">
                    <p className="text-ink">{k}</p>
                    <p className="text-graphite">{hex}</p>
                  </div>
                </div>
              );
            })}
          </div>
          {colors.neutral_scale && colors.neutral_scale.length > 0 && (
            <div className="flex gap-1 mt-2">
              {colors.neutral_scale.map((hex, i) => (
                <span
                  key={i}
                  className="inline-block h-6 w-12 border border-ink/10"
                  style={{ background: hex }}
                  title={hex}
                  aria-hidden
                />
              ))}
            </div>
          )}
        </section>

        <section className="space-y-2">
          <p className="smallcaps text-graphite">Typography</p>
          <p className="text-body text-ink">
            <span className="font-mono text-[11px] text-graphite">heading</span> {ds.typography?.heading_font || '—'}
            <span className="mx-3 text-graphite">·</span>
            <span className="font-mono text-[11px] text-graphite">body</span> {ds.typography?.body_font || '—'}
          </p>
          {ds.typography?.scale && (
            <p className="font-mono text-[11px] text-graphite">scale: {ds.typography.scale.join(' · ')}</p>
          )}
        </section>

        <section className="space-y-3">
          <p className="smallcaps text-graphite">Pages — {payload.pages?.length ?? 0}</p>
          <div className="space-y-3">
            {(payload.pages ?? []).map((p) => (
              <div key={p.label} className="rounded-sm border border-ink/10 p-4">
                <div className="flex items-baseline justify-between mb-2">
                  <h3 className="font-serif text-xl italic text-ink">{p.label}</h3>
                  <code className="font-mono text-[11px] text-graphite">{p.route || '—'}</code>
                </div>
                {p.layout_plan && p.layout_plan.length > 0 && (
                  <div className="mt-2">
                    <p className="smallcaps text-graphite mb-1.5">Layout plan</p>
                    <ul className="space-y-0.5">
                      {p.layout_plan.map((slot, i) => (
                        <li key={i} className="font-mono text-[12px] text-ink">
                          <span className="text-graphite">{slot.role || '—'}</span> · {slot.component_kg_id}
                          {slot.notes ? <span className="text-graphite"> — {slot.notes}</span> : null}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {p.law_references && p.law_references.length > 0 && (
                  <p className="mt-2 font-mono text-[10px] text-graphite">
                    Laws satisfied: {p.law_references.join(', ')}
                  </p>
                )}
              </div>
            ))}
          </div>
        </section>

        {payload.asset_manifest && payload.asset_manifest.length > 0 && (
          <section className="space-y-2">
            <p className="smallcaps text-graphite">Asset manifest — {payload.asset_manifest.length}</p>
            <ul className="divide-y divide-ink/5">
              {payload.asset_manifest.map((a) => (
                <li key={a.name} className="flex items-start gap-3 py-2">
                  <span className="font-mono text-[11px] text-ink w-40 truncate">{a.name}</span>
                  <span className="font-mono text-[11px] text-graphite w-32">{a.usage}</span>
                  <span className="text-[12px] text-graphite italic flex-1">{a.prompt}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        <CheckpointActions
          jobId={jobId}
          outputId={output._id}
          agent="loom"
          disabled={output.reviewStatus !== 'awaiting_user'}
        />
      </div>
    </ReviewPanel>
  );
}
